import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    cvr: '',
    description: '',
    website_url: '' // Honeypot
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes';
    } else if (!/^[0-9\s+()-]{8,20}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig e-mailadresse';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse skal udfyldes';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Beskrivelsen skal være på mindst 10 tegn';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          cvr: formData.cvr,
          description: formData.description,
          website_url: formData.website_url // Honeypot field
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          company: '',
          cvr: '',
          description: '',
          website_url: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm border border-slate-100 p-8 md:p-12 shadow-none">
      <h3 className="font-display text-2xl font-medium text-navy-950 mb-6">
        Send os en besked
      </h3>
      <p className="text-sm text-slate-500 mb-8">
        Udfyld formularen nedenfor, og vi vil vende tilbage til dig hurtigst muligt.
      </p>

      {submitStatus === 'success' && (
        <div className="mb-8 p-6 bg-emerald-50 border border-emerald-100 text-emerald-800 rounded-none flex items-start gap-3">
          <i className="fa-solid fa-circle-check text-emerald-500 mt-1" aria-hidden="true" />
          <div>
            <h4 className="font-semibold text-sm">Tak for din henvendelse</h4>
            <p className="text-xs text-emerald-700 mt-1">
              Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt.
            </p>
          </div>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-8 p-6 bg-rose-50 border border-rose-100 text-rose-800 rounded-none flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation text-rose-500 mt-1" aria-hidden="true" />
          <div>
            <h4 className="font-semibold text-sm">Der opstod en fejl</h4>
            <p className="text-xs text-rose-700 mt-1">
              Kunne ikke sende din besked. Prøv venligst igen senere eller kontakt os direkte via telefon eller e-mail.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Honeypot field - absolutely hidden from humans */}
        <div className="sr-only absolute" aria-hidden="true">
          <label htmlFor="website_url">Website URL</label>
          <input
            id="website_url"
            type="text"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
            placeholder="Do not fill this out"
          />
        </div>

        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-navy-950 mb-2">
            Navn <span className="text-gold-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-none text-sm transition-colors duration-300 focus:outline-none focus:border-gold-500 ${
              errors.name ? 'border-rose-400 bg-rose-50/10' : 'border-slate-200'
            }`}
            placeholder="Dit fulde navn"
            disabled={isSubmitting}
          />
          {errors.name && (
            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
              <i className="fa-solid fa-circle-exclamation text-[10px]" /> {errors.name}
            </p>
          )}
        </div>

        {/* Grid for Phone & Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-navy-950 mb-2">
              Telefonnummer <span className="text-gold-500">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-none text-sm transition-colors duration-300 focus:outline-none focus:border-gold-500 ${
                errors.phone ? 'border-rose-400 bg-rose-50/10' : 'border-slate-200'
              }`}
              placeholder="F.eks. 50 358 378"
              disabled={isSubmitting}
            />
            {errors.phone && (
              <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
                <i className="fa-solid fa-circle-exclamation text-[10px]" /> {errors.phone}
              </p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-navy-950 mb-2">
              E-mail <span className="text-gold-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-none text-sm transition-colors duration-300 focus:outline-none focus:border-gold-500 ${
                errors.email ? 'border-rose-400 bg-rose-50/10' : 'border-slate-200'
              }`}
              placeholder="din@email.dk"
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
                <i className="fa-solid fa-circle-exclamation text-[10px]" /> {errors.email}
              </p>
            )}
          </div>
        </div>

        {/* Grid for Company Name & CVR */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Company Name Field */}
          <div>
            <label htmlFor="company" className="block text-xs font-semibold uppercase tracking-wider text-navy-950 mb-2">
              Virksomheds navn
            </label>
            <input
              id="company"
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-slate-200 rounded-none text-sm transition-colors duration-300 focus:outline-none focus:border-gold-500"
              placeholder="F.eks. KnowHow GROUP"
              disabled={isSubmitting}
            />
          </div>

          {/* CVR Field */}
          <div>
            <label htmlFor="cvr" className="block text-xs font-semibold uppercase tracking-wider text-navy-950 mb-2">
              CVR nr.
            </label>
            <input
              id="cvr"
              type="text"
              name="cvr"
              value={formData.cvr}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-slate-200 rounded-none text-sm transition-colors duration-300 focus:outline-none focus:border-gold-500"
              placeholder="8-cifret CVR-nummer"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Description Field */}
        <div>
          <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-navy-950 mb-2">
            Beskrivelse <span className="text-gold-500">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            rows="5"
            value={formData.description}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-none text-sm transition-colors duration-300 focus:outline-none focus:border-gold-500 resize-none ${
              errors.description ? 'border-rose-400 bg-rose-50/10' : 'border-slate-200'
            }`}
            placeholder="Beskriv din henvendelse eller dit projekt..."
            disabled={isSubmitting}
          />
          {errors.description && (
            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
              <i className="fa-solid fa-circle-exclamation text-[10px]" /> {errors.description}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-gold-500 text-white text-xs font-bold uppercase tracking-[0.2em] rounded-none hover:bg-navy-950 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:ring-offset-2 cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner-third animate-spin" />
              Sender...
            </>
          ) : (
            <>
              Send henvendelse
              <i className="fa-light fa-arrow-right-long" />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
